import { alpha } from '@mui/material/styles';
import {
  BOOKING_CHECKED_IN_COLOR,
  BOOKING_CONFIRMED_COLOR,
  getBookingStatusColorKey,
} from './bookingOpsCardHelpers';

export function getBookingOpsCardColors(statusColor, isDark, statusValue = '') {
  const colorKey = getBookingStatusColorKey(statusValue);
  const preArrival = colorKey === 'confirmed';
  const inHouse = colorKey === 'checked_in' || colorKey === 'partial_checkin' || colorKey === 'partial_checkout';

  const base = {
    dot: statusColor,
    border: alpha(statusColor, isDark ? 0.32 : 0.28),
    shadow: isDark ? '0 6px 18px rgba(0,0,0,0.32)' : '0 2px 8px rgba(15,23,42,0.06)',
  };

  if (preArrival) {
    return {
      ...base,
      dot: statusColor || BOOKING_CONFIRMED_COLOR,
      border: alpha(statusColor || BOOKING_CONFIRMED_COLOR, isDark ? 0.32 : 0.28),
      bg: isDark
        ? undefined
        : 'linear-gradient(165deg, #fff7ed 0%, #ffffff 100%)',
      heroBg: isDark
        ? undefined
        : `radial-gradient(circle at 50% 0%, ${alpha(statusColor || BOOKING_CONFIRMED_COLOR, 0.12)} 0%, transparent 58%)`,
      heroGlow: isDark ? 0.09 : 0.1,
    };
  }

  if (inHouse) {
    return {
      ...base,
      dot: statusColor || BOOKING_CHECKED_IN_COLOR,
      border: alpha(statusColor || BOOKING_CHECKED_IN_COLOR, isDark ? 0.32 : 0.28),
      bg: isDark
        ? undefined
        : 'linear-gradient(165deg, #e8f5e9 0%, #ffffff 100%)',
      heroBg: isDark
        ? undefined
        : `radial-gradient(circle at 50% 0%, ${alpha(statusColor || BOOKING_CHECKED_IN_COLOR, 0.12)} 0%, transparent 58%)`,
      heroGlow: isDark ? 0.1 : 0.12,
    };
  }

  return base;
}

export function getBookingOpsCodeTextSx(accent, isDark) {
  return {
    position: 'absolute',
    top: 10,
    right: 10,
    zIndex: 2,
    maxWidth: '46%',
    fontWeight: 700,
    fontSize: '0.62rem',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    letterSpacing: '0.02em',
    color: isDark ? alpha(accent, 0.88) : '#475569',
    textAlign: 'right',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };
}

export function getBookingOpsRoomTypesTextSx(isDark) {
  return {
    mt: 0.35,
    px: 0.75,
    maxWidth: '100%',
    textAlign: 'center',
    fontSize: '0.68rem',
    fontWeight: 500,
    lineHeight: 1.35,
    color: isDark ? '#94a3b8' : '#64748b',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  };
}

export function getBookingOpsClassicStayRowSx(isDark) {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: 0.5,
    py: 0.75,
    color: isDark ? '#cbd5e1' : '#475569',
    fontSize: '0.72rem',
    fontWeight: 600,
  };
}

export function getBookingOpsClassicMetaDotSx(isDark) {
  return {
    color: isDark ? alpha('#ffffff', 0.22) : alpha('#000', 0.22),
    userSelect: 'none',
  };
}

export function getBookingOpsClassicPaymentRowSx(isDark, isDue) {
  return {
    mt: 'auto',
    pt: 0.85,
    borderTop: `1px solid ${isDark ? alpha('#ffffff', 0.08) : alpha('#000', 0.08)}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 0.75,
  };
}

export function getBookingOpsClassicNoticeSx(isDark) {
  return {
    mt: 0.5,
    textAlign: 'center',
    fontSize: '0.65rem',
    fontWeight: 600,
    lineHeight: 1.35,
    color: '#ff9800',
  };
}

export function getBookingOpsDueChipSx(isDark) {
  return {
    height: 22,
    fontSize: '0.62rem',
    fontWeight: 800,
    letterSpacing: '0.02em',
    bgcolor: isDark ? alpha('#ff7043', 0.14) : alpha('#ff7043', 0.1),
    color: '#ff7043',
    border: `1px solid ${alpha('#ff7043', isDark ? 0.35 : 0.28)}`,
    '& .MuiChip-label': { px: 0.75 },
  };
}

export function getBookingOpsCodeChipSx(accent, isDark) {
  const lightText = '#065f46';
  const lightBg = '#ecfdf5';
  const lightBorder = '#6ee7b7';

  return {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 2,
    height: 22,
    maxWidth: '46%',
    fontWeight: 800,
    fontSize: '0.62rem',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    letterSpacing: '0.02em',
    bgcolor: isDark ? alpha('#020617', 0.88) : lightBg,
    color: isDark ? accent : lightText,
    border: `1px solid ${isDark ? alpha(accent, 0.45) : lightBorder}`,
    boxShadow: isDark
      ? `0 4px 12px rgba(0,0,0,0.28)`
      : `0 1px 2px ${alpha('#065f46', 0.08)}, inset 0 1px 0 ${alpha('#ffffff', 0.85)}`,
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    '& .MuiChip-label': { px: 0.7 },
  };
}

export function getBookingOpsRoomTypeChipSx(accent, isDark) {
  const lightText = '#065f46';
  const lightBg = '#d1fae5';
  const lightBorder = '#34d399';

  return {
    height: 24,
    fontSize: '0.64rem',
    fontWeight: 800,
    letterSpacing: '0.01em',
    bgcolor: isDark ? alpha(accent, 0.14) : lightBg,
    color: isDark ? accent : lightText,
    border: `1px solid ${isDark ? alpha(accent, 0.42) : lightBorder}`,
    boxShadow: isDark
      ? `inset 0 1px 0 ${alpha('#ffffff', 0.06)}`
      : `0 1px 2px ${alpha('#065f46', 0.08)}`,
    '& .MuiChip-label': { px: 0.85 },
  };
}

export function getBookingOpsStayPanelSx(isDark, accent) {
  return {
    mt: 0.75,
    p: 0.85,
    borderRadius: 1.5,
    bgcolor: isDark ? alpha('#ffffff', 0.04) : alpha(accent, 0.04),
    border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : alpha(accent, 0.12)}`,
  };
}

export function getBookingOpsStatPillSx(isDark, color) {
  return {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.45,
    py: 0.45,
    borderRadius: 1.25,
    bgcolor: isDark ? alpha(color, 0.12) : alpha(color, 0.08),
    border: `1px solid ${alpha(color, isDark ? 0.22 : 0.16)}`,
  };
}

export function getBookingOpsPaymentPanelSx(isDark, isDue) {
  const color = isDue ? '#ff7043' : '#4ce1b6';
  return {
    mt: 0.75,
    px: 0.9,
    py: 0.75,
    borderRadius: 1.5,
    bgcolor: isDark ? alpha(color, 0.1) : alpha(color, 0.06),
    border: `1px solid ${alpha(color, isDark ? 0.28 : 0.2)}`,
  };
}

export function getBookingOpsPartialNoticeSx(isDark) {
  return {
    mt: 0.65,
    px: 0.75,
    py: 0.45,
    borderRadius: 1.25,
    textAlign: 'center',
    bgcolor: alpha('#ff9800', isDark ? 0.12 : 0.08),
    border: `1px solid ${alpha('#ff9800', 0.35)}`,
  };
}

export function normalizeRoomChipLabel(value) {
  return String(value || '')
    .replace(/^\[/, '')
    .replace(/\]$/, '')
    .replace(/^Room\s+/i, '')
    .trim();
}

export function extractRoomLabelFromRecord(record) {
  if (!record) return null;
  return (
    record.allocated_room_number
    || record.allocated_room?.room_number
    || record.allocated_room?.number
    || record.room_number
    || record.assigned_room_number
    || record.assigned_room?.room_number
    || record.assigned_room?.number
    || null
  );
}

export function getPrimaryRoomLabel(roomChips) {
  if (!roomChips?.length) return null;

  const numeric = roomChips.find((chip) => /^\d+$/.test(normalizeRoomChipLabel(chip.label)));
  if (numeric) return normalizeRoomChipLabel(numeric.label);

  for (const chip of roomChips) {
    const raw = String(chip?.label || '').trim();
    if (!raw || raw.includes('->')) continue;
    const normalized = normalizeRoomChipLabel(raw);
    if (normalized) return normalized;
  }

  return null;
}

export function resolvePrimaryRoomLabel(booking, roomChips = []) {
  const fromChips = getPrimaryRoomLabel(roomChips);
  if (fromChips) return fromChips;

  const items = Array.isArray(booking?.booking_items) ? booking.booking_items : [];
  for (const item of items) {
    const label = extractRoomLabelFromRecord(item);
    if (label) return normalizeRoomChipLabel(label);
  }

  const allocatedRooms = Array.isArray(booking?.allocated_rooms) ? booking.allocated_rooms : [];
  for (const room of allocatedRooms) {
    const label = extractRoomLabelFromRecord(room);
    if (label) return normalizeRoomChipLabel(label);
  }

  const topLevel = extractRoomLabelFromRecord(booking);
  if (topLevel) return normalizeRoomChipLabel(topLevel);

  return null;
}

export function getAdditionalRoomChips(roomChips = [], primaryRoomLabel = null) {
  if (!roomChips?.length) return [];

  const primaryNorm = primaryRoomLabel ? normalizeRoomChipLabel(primaryRoomLabel) : null;
  const seen = new Set();
  const additional = [];

  roomChips.forEach((chip) => {
    const raw = String(chip?.label || '').trim();
    if (!raw) return;

    const isLink = raw.includes('->');
    const norm = normalizeRoomChipLabel(raw);

    if (isLink) {
      const parts = norm.split('->').map((part) => normalizeRoomChipLabel(part)).filter(Boolean);
      const uniqueParts = [...new Set(parts)];
      if (uniqueParts.length <= 1) return;
      if (primaryNorm && uniqueParts.every((part) => part === primaryNorm)) return;
    } else if (primaryNorm && norm === primaryNorm) {
      return;
    }

    const dedupeKey = isLink ? raw : norm;
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    additional.push(chip);
  });

  return additional;
}

export function resolveBookingRoomLineItems(booking, roomChips = []) {
  const segments = [];
  const seenRooms = new Set();

  const pushRoom = (label, keySuffix = '') => {
    const norm = normalizeRoomChipLabel(label);
    if (!norm || seenRooms.has(norm)) return;
    seenRooms.add(norm);
    segments.push({ type: 'room', label: norm, key: `room-${norm}${keySuffix}` });
  };

  const pushLink = (raw, keySuffix = '') => {
    const norm = normalizeRoomChipLabel(raw);
    const parts = norm.split('->').map((part) => normalizeRoomChipLabel(part)).filter(Boolean);
    const uniqueParts = [...new Set(parts)];
    if (uniqueParts.length >= 2) {
      uniqueParts.forEach((part) => seenRooms.add(part));
      segments.push({
        type: 'link',
        label: uniqueParts.join(' → '),
        key: `link-${uniqueParts.join('-')}${keySuffix}`,
      });
      return;
    }
    if (uniqueParts.length === 1) {
      pushRoom(uniqueParts[0], keySuffix);
    }
  };

  if (roomChips?.length) {
    roomChips.forEach((chip, index) => {
      const raw = String(chip?.label || '').trim();
      if (!raw) return;
      if (raw.includes('->')) {
        const isUpgradeLink = raw.startsWith('[');
        const norm = normalizeRoomChipLabel(raw);
        const parts = norm.split('->').map((part) => normalizeRoomChipLabel(part)).filter(Boolean);
        const uniqueParts = [...new Set(parts)];
        if (isUpgradeLink || uniqueParts.length === 2) {
          pushLink(raw, `-${index}`);
        } else {
          uniqueParts.forEach((part, partIndex) => pushRoom(part, `-${index}-${partIndex}`));
        }
      } else {
        pushRoom(raw, `-${index}`);
      }
    });
  }

  const expectedRoomCount = Number(booking?.rooms || booking?.total_rooms || 0);
  if (expectedRoomCount > segments.length && expectedRoomCount > 3) {
    const items = Array.isArray(booking?.booking_items) ? booking.booking_items : [];
    items.forEach((item, index) => {
      const label = extractRoomLabelFromRecord(item);
      if (label) pushRoom(label, `-item-supplement-${index}`);
    });
  }

  if (segments.length === 0) {
    const items = Array.isArray(booking?.booking_items) ? booking.booking_items : [];
    items.forEach((item, index) => {
      const label = extractRoomLabelFromRecord(item);
      if (label) pushRoom(label, `-item-${index}`);
    });

    const allocatedRooms = Array.isArray(booking?.allocated_rooms) ? booking.allocated_rooms : [];
    allocatedRooms.forEach((room, index) => {
      const label = extractRoomLabelFromRecord(room);
      if (label) pushRoom(label, `-alloc-${index}`);
    });

    const topLevel = extractRoomLabelFromRecord(booking);
    if (topLevel) pushRoom(topLevel, '-top');
  }

  return segments;
}

export function getBookingOpsRoomLineSx(isMobile, roomCount = 1) {
  const isCompact = roomCount > 3;
  const isDense = roomCount > 8;

  return {
    mt: isMobile ? 0.35 : 0.4,
    px: 0.5,
    maxWidth: '100%',
    width: '100%',
    flexWrap: isCompact ? 'wrap' : 'nowrap',
    overflow: isCompact ? 'visible' : 'hidden',
    justifyContent: 'center',
    rowGap: isDense ? 0.2 : 0.3,
    columnGap: isDense ? 0.3 : isCompact ? 0.4 : 0.55,
  };
}

export function getBookingOpsRoomNumberSegmentSx(isMobile, isDark, colors, segmentCount = 1, isLink = false) {
  if (isLink) {
    const linkFontSize = segmentCount > 8
      ? (isMobile ? '0.62rem' : '0.68rem')
      : segmentCount > 4
        ? (isMobile ? '0.72rem' : '0.78rem')
        : (isMobile ? '0.78rem' : '0.84rem');

    return {
      fontWeight: 700,
      fontSize: linkFontSize,
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
      whiteSpace: 'nowrap',
      color: isDark ? alpha(colors.dot, 0.92) : colors.dot,
    };
  }

  let fontSize;
  if (segmentCount <= 2) {
    fontSize = isMobile ? '1.35rem' : '1.5rem';
  } else if (segmentCount <= 4) {
    fontSize = isMobile ? '1.05rem' : '1.15rem';
  } else if (segmentCount <= 8) {
    fontSize = isMobile ? '0.88rem' : '0.95rem';
  } else if (segmentCount <= 12) {
    fontSize = isMobile ? '0.72rem' : '0.78rem';
  } else {
    fontSize = isMobile ? '0.62rem' : '0.68rem';
  }

  return {
    fontWeight: segmentCount > 8 ? 700 : 800,
    fontSize,
    lineHeight: 1.15,
    letterSpacing: segmentCount > 8 ? 0 : '-0.02em',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
    color: isDark ? '#f8fafc' : '#0f172a',
  };
}

export function getBookingOpsRoomLineSeparatorSx(isDark, colors, roomCount = 1) {
  const isDense = roomCount > 8;
  const isCompact = roomCount > 3;

  return {
    color: isDark ? alpha('#ffffff', 0.28) : alpha(colors.dot, 0.45),
    fontWeight: 300,
    fontSize: isDense ? '0.62rem' : isCompact ? '0.78rem' : '1rem',
    lineHeight: 1,
    userSelect: 'none',
  };
}
